import { createFileRoute } from '@tanstack/react-router';
import { productIdPathParams } from '@/lib/product-url-codec';
import { useQueries } from '@tanstack/react-query';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { formatDuration, formatServerDateShort, parseServerDate } from '@/lib/dates';
import { useProducts } from '@/features/product/hooks';
import { useDossierRun, useDossierDispatchDetail } from '@/features/dossier/hooks';
import { getDossierRun } from '@/features/dossier/api';
import type { DossierRunDetail } from '@/features/dossier/api';
import { DossierDispatchForm } from '@/features/dossier/components/DossierDispatchForm';
import { CombinedEuDossierView } from '@/features/dossier/components/CombinedEuDossierView';
import { CombinedNiIvdrDossierView } from '@/features/dossier/components/CombinedNiIvdrDossierView';
import { DossierResultRenderer } from '@/features/dossier/components/DossierResultRenderer';
import { DossierRunProvenanceContext } from '@/features/dossier/dossier-run-provenance';
import { TraceabilityMatrix } from '@/features/dossier/components/TraceabilityMatrix';
import { PageHero } from '@/components/layout/PageHero';
import { DispatchTabsShell } from '@/features/agent-dispatch/DispatchTabsShell';
import type { DispatchChildStatus } from '@/features/agent-dispatch/types';
import { QueuedBanner } from '@/features/agent-dispatch/QueuedBanner';
import { FailedBanner as DispatchFailedBanner } from '@/features/agent-dispatch/FailedBanner';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import type { DispatchData, DispatchChild } from '@/features/agent-dispatch/types';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';
import {
  groupRunsByCountry,
  aggregateStatus,
  mergeProvenanceManifests,
  tabIdOf,
  tabRunIdFor,
} from '@/features/dossier/groupDispatchRuns';
import { dispatchSubtitleFor, workflowForRun } from '@/features/dossier/workflowSteps';
import { combinedDossierViewFor } from '@/features/dossier/combinedDossierView';
import { CanadaDossierView } from '@/features/dossier/components/CanadaDossierView';
import { UkDossierView } from '@/features/dossier/components/UkDossierView';
import { UkIvdDossierView } from '@/features/dossier/components/UkIvdDossierView';
import { AuDossierView } from '@/features/dossier/components/AuDossierView';
import { AuIvdDossierView } from '@/features/dossier/components/AuIvdDossierView';
import { isGbIvdSubmissionType } from '@/features/dossier/uk-submission-pathways';

/**
 * Submission Dossier — dispatch + polling + result page.
 *
 * URL: /submission-dossier/:productId?dossier_run_id=<n>
 *
 * Phase A (this commit): polling chrome (run-card, workflow stepper,
 * completion banner) + a placeholder for the dispatch form and the
 * structured result renderer.
 *
 * Phase B will add the dispatch form (submission-type select + 4 optional
 * file uploads with per-pathway required-ness gating).
 * Phase C will add the structured renderer (sections, predicate-comparison
 * table, sources, eSTAR fields — porting the 977-LoC legacy renderer).
 */

const searchSchema = z.object({
  dossier_run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const DOSSIER_STEP_SECONDS = 30;

// Canonical NI IVDR classes (as normalised by the BE dispatch policy).
// NI MDR classes (IIa/IIb/IIb_implantable/III) fall through to CombinedEuDossierView
// which already handles the MDR profile via its IVDR_CLASSES set.
const NI_IVDR_CLASSES = new Set(['A_sterile', 'B', 'C', 'D']);


export const Route = createFileRoute('/submission-dossier_/$productId')({
  // Opaque token in the address bar, never the global product id.
  params: productIdPathParams,
  validateSearch: searchSchema,
  component: SubmissionDossierResultPage,
});

function SubmissionDossierResultPage() {
  const { productId } = Route.useParams();
  const { dossier_run_id: runId } = Route.useSearch();
  const products = useProducts();
  const product = (products.data ?? []).find((p) => String(p.id) === productId) ?? null;
  const { data: run, isLoading, isError } = useDossierRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Submission Dossier"
        // Before a run, the dispatch page is worded for the product's type; a run
        // page is worded for its pathway.
        subtitle={
          runId === undefined
            ? dispatchSubtitleFor(product?.product_type)
            : workflowForRun(run).subtitle
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-7 pb-20">
        {runId === undefined && (
          <>
            {products.isLoading && (
              <RunLoadingSkeleton label="Loading product" />
            )}
            {!products.isLoading && !product && (
              <NotFoundCard />
            )}
            {product && <DossierDispatchForm product={product} />}
          </>
        )}

        {runId !== undefined && isLoading && (
          <RunLoadingSkeleton />
        )}
        {runId !== undefined && isError && (
          <ErrorCard message="Could not load the run. Please refresh." />
        )}
        {runId !== undefined && run && (
          run.dispatch_group_id ? (
            // ``initialRunId`` opens the tabs ON the linked run's country, as the other agents'
            // result pages do (a link to an EU part opens the EU tab).
            <DispatchView groupId={run.dispatch_group_id} initialRunId={run.id} />
          ) : (
            <SingleRunResult run={run} />
          )
        )}
        {/* The disclaimer is co-located with each completed-content renderer
            (CompletedSection / the EU combined view), so it appears strictly
            under a finished dossier — never under a queued / in-progress / failed
            tab. (A page-level guard on the URL run leaked it onto a queued tab
            when the URL run happened to be a completed sibling.) */}
      </main>
      <ScrollToTopButton />
    </>
  );
}

function NotFoundCard() {
  return (
    <div className="bg-white border border-line rounded-2xl p-12 text-center shadow-card">
      <p className="text-navy text-lg font-semibold m-0 mb-2">Product not found</p>
      <p className="text-muted text-sm m-0 mb-6">
        This product doesn't exist or you don't have access to it.
      </p>
      <Button variant="outline" asChild>
        <a href="/submission-dossier">← Back to product list</a>
      </Button>
    </div>
  );
}

/** Result body for a single (non-grouped) run. A country with an in-page
 *  combined-dossier view (resolved generically — today EU) renders it + the
 *  traceability matrix once complete; everything else shows the generic run
 *  card + state body. The route no longer hardcodes `country === 'EU'`. */
/** A run from before dispatches were grouped (no dispatch_group_id). Its run card shows in every
 *  state, the finished combined dossier included, like every dispatch (Marc, 2026-10-21: every EU
 *  device, IVDR, drug and biologic pathway). */
export function SingleRunResult({ run }: { run: DossierRunDetail }) {
  const combined =
    run.status === 'completed' && run.product_id != null && run.submission_type
      ? combinedDossierViewFor(run.country, {
          productId: run.product_id,
          deviceClass: run.submission_type,
          runId: run.id,
        })
      : null;
  if (combined) {
    return (
      <>
        <AgentRunCard run={run} agentLabel="Dossier run" />
        {combined}
        <TraceabilityMatrix manifest={run.provenance} />
        <ResultDisclaimer />
      </>
    );
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel="Dossier run" />
      <RunStateBody run={run} />
    </>
  );
}

function RunStateBody({ run, country }: { run: DossierRunDetail; country?: string | null }) {
  if (run.status === 'queued') {
    return <QueuedBanner country={country} />;
  }
  if (run.status === 'running') {
    return (
      <AgentPollingPanel
        startedAt={run.started_at}
        workflowSteps={workflowForRun(run).steps}
        stepSeconds={DOSSIER_STEP_SECONDS}
        title={
          country ? (
            <>
              Dossier Drafting in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          ) : (
            'Dossier Drafting in Progress'
          )
        }
        subtitle="Compiling the regulatory submission dossier via the REGARA AI agent."
      />
    );
  }

  if (run.status === 'failed') {
    return (
      <DispatchFailedBanner
        country={country}
        message={friendlyErrorMessage(run.error_message)}
      />
    );
  }

  return <CompletedSection run={run} country={country} />;
}

/** Multi-country view. Polls every sibling run in the dispatch group and
 *  renders ONE tab per country. EU's complete-dossier annexes (each its own
 *  agent run) are collapsed into a single "European Union" tab that shows a
 *  unified progress indicator while generating, and then the full combined
 *  dossier (download + each annex rendered in-page as a labeled section)
 *  when all annexes complete. */
function DispatchView({ groupId, initialRunId }: { groupId: string; initialRunId: number | null }) {
  const { data, isLoading, isError } = useDossierDispatchDetail(groupId);
  const groups = data ? groupRunsByCountry(data.runs) : [];
  const shellData: DispatchData | undefined = data
    ? {
        dispatch_group_id: data.dispatch_group_id,
        runs: groups.map((g) => ({
          id: tabIdOf(g),
          country: g.country,
          status: aggregateStatus(g.runs.map((r) => r.status)),
        })),
      }
    : undefined;
  const runsByCountry = new Map(groups.map((g) => [g.country, g.runs]));
  const bodyFor = (repId: number, country: string | null) => {
    const childRuns = runsByCountry.get(country) ?? [];
    return childRuns.length > 1 ? (
      <EuGroupedDossierBody childRuns={childRuns} country={country} />
    ) : (
      <DossierRunBody runId={repId} country={country} />
    );
  };
  return (
    <DispatchTabsShell
      data={shellData}
      isLoading={isLoading}
      isError={isError}
      initialRunId={tabRunIdFor(groups, initialRunId)}
      renderRunMeta={(runId, aggregateStatus) => (
        <DossierRunMetaCard runId={runId} aggregateStatus={aggregateStatus} />
      )}
      renderSingleRun={(runId) => {
        const onlyCountry = groups[0]?.country ?? null;
        return (
          <SingleCountryDispatchRun
            runId={runId}
            childRuns={runsByCountry.get(onlyCountry) ?? []}
            country={onlyCountry}
          />
        );
      }}
      renderRunBody={bodyFor}
    />
  );
}

/** A dispatch to one country: no tabs, so the shell's identity card (drawn above the tabs) never
 *  shows. A grouped country (EU / NI: several annex runs) draws that one card itself, with the
 *  group's aggregate status, above its body (Marc, 2026-10-21: an EU-only dossier showed no run card);
 *  a single run's own body already carries its card. */
export function SingleCountryDispatchRun({
  runId,
  childRuns,
  country,
}: {
  runId: number;
  childRuns: DispatchChild[];
  country: string | null;
}) {
  if (childRuns.length > 1) {
    return (
      <>
        <DossierRunMetaCard runId={runId} aggregateStatus={aggregateStatus(childRuns.map((r) => r.status))} />
        <EuGroupedDossierBody childRuns={childRuns} country={country} />
      </>
    );
  }
  return <DossierSingleRun runId={runId} />;
}

function DossierRunMetaCard({
  runId,
  aggregateStatus,
}: {
  runId: number;
  aggregateStatus?: DispatchChildStatus;
}) {
  const { data: run } = useDossierRun(runId);
  if (!run) return null;
  // Grouped countries (EU/NI) collapse several annex runs into one tab; show the
  // AGGREGATE status so the identity card reads "Running" while any annex still
  // drafts (not the first annex's own "Completed"). This card is the single
  // identity card for the view — the per-tab bodies render none of their own.
  const shown = aggregateStatus ? { ...run, status: aggregateStatus } : run;
  return <AgentRunCard run={shown} agentLabel="Dossier run" />;
}

function DossierSingleRun({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useDossierRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel="Dossier run" />
      <RunStateBody run={run} />
    </>
  );
}

function DossierRunBody({ runId, country }: { runId: number; country: string | null }) {
  const { data: run, isLoading, isError } = useDossierRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton label="Loading" />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load this country's run. Please refresh." />;
  }
  return <RunStateBody run={run} country={country} />;
}

export function CompletedSection({
  run,
  country,
}: {
  run: DossierRunDetail;
  country?: string | null;
}) {
  const startedAt = parseServerDate(run.started_at);
  const completedAt = parseServerDate(run.completed_at);
  const durationSec =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  const response = run.dossier_response;
  // Pick the country from the run itself — the single-run path doesn't thread a
  // ``country`` prop (it stays generic so the in-progress view is uniform), but
  // the COMPLETED body must still resolve the right per-country renderer. CA
  // renders its own 6-chapter view + its Health Canada (SOR/98-282 / IMDRF)
  // traceability matrix — framed by run.provenance's CA profile, never FDA.
  const resolvedCountry = (country ?? run.country ?? '').toUpperCase();

  return (
    <DossierRunProvenanceContext.Provider
      value={{ generatedAt: run.completed_at, generatedBy: run.created_by_name }}
    >
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              Dossier Complete
              {country && (
                <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                  {countryLabel(country)}
                </span>
              )}
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {formatServerDateShort(run.completed_at)}
              {durationSec !== null && ` · ${formatDuration(durationSec)}`}
            </p>
          </div>
        </div>
      </div>

      {response ? (
        resolvedCountry === 'CA' ? (
          <>
            <CanadaDossierView response={response} runId={run.id} />
            <TraceabilityMatrix manifest={run.provenance} />
          </>
        ) : resolvedCountry === 'UK' ? (
          isGbIvdSubmissionType(run.submission_type) ||
          (run.document_type ?? '').startsWith('uk_gb_ivdr_') ? (
            <>
              <UkIvdDossierView response={response} runId={run.id} />
              <TraceabilityMatrix manifest={run.provenance} />
            </>
          ) : (
            <>
              <UkDossierView response={response} runId={run.id} />
              <TraceabilityMatrix manifest={run.provenance} />
            </>
          )
        ) : resolvedCountry === 'AU' ? (
          (run.document_type ?? '').startsWith('au_ivd_') ? (
            <>
              <AuIvdDossierView response={response} runId={run.id} />
              <TraceabilityMatrix manifest={run.provenance} />
            </>
          ) : (
            <>
              <AuDossierView response={response} runId={run.id} />
              <TraceabilityMatrix manifest={run.provenance} />
            </>
          )
        ) : (
          <>
            <DossierResultRenderer response={response} runId={run.id} country={resolvedCountry} />
            <TraceabilityMatrix manifest={run.provenance} />
          </>
        )
      ) : (
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
          <div className="font-semibold text-state-error-text text-lg">
            Could not parse the dossier response
          </div>
          <p className="text-sm text-state-error-text mt-1">
            The run completed but the dossier payload was not delivered. Try generating
            a new dossier.
          </p>
        </div>
      )}
      <ResultDisclaimer />
    </DossierRunProvenanceContext.Provider>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
      {message}
    </div>
  );
}

/** One country's complete EU dossier, assembled from its annex part-runs into a
 *  single tab: ONE header card + annex bodies flowing continuously + ONE merged
 *  traceability matrix. Unified progress while generating; full dossier when
 *  all annexes complete. */
export function EuGroupedDossierBody({
  childRuns,
  country,
}: {
  childRuns: DispatchChild[];
  country: string | null;
}) {
  const agg = aggregateStatus(childRuns.map((r) => r.status));
  // Representative run — used for product_id + submission_type + started_at.
  const rep = useDossierRun(childRuns[0]?.id ?? null);

  // Fetch each annex run's full detail to merge their provenance manifests.
  // Uses the same queryKey shape as useDossierRun so results are cache-shared.
  const annexDetails = useQueries({
    queries: childRuns.map((r) => ({
      queryKey: ['dossier-run', r.id] as const,
      queryFn: () => getDossierRun(r.id),
      enabled: agg === 'completed',
      retry: 1,
    })),
  });
  const mergedProvenance =
    agg === 'completed'
      ? mergeProvenanceManifests(annexDetails.map((q) => q.data?.provenance ?? null))
      : null;

  // Completed timestamp + elapsed across the annexes (earliest start → latest
  // completion) so the EU grouped banner shows date + duration like the
  // single-country CompletedSection (US / CA). ISO strings sort chronologically.
  const annexRuns = annexDetails
    .map((q) => q.data)
    .filter((r): r is DossierRunDetail => !!r);
  const lastCompletedAt =
    annexRuns.map((r) => r.completed_at).filter(Boolean).slice().sort().at(-1) ?? null;
  const firstStartedAt =
    annexRuns.map((r) => r.started_at).filter(Boolean).slice().sort().at(0) ?? null;
  const groupDurationSec =
    firstStartedAt && lastCompletedAt
      ? Math.round(
          (parseServerDate(lastCompletedAt)!.getTime() -
            parseServerDate(firstStartedAt)!.getTime()) /
            1000,
        )
      : null;

  // NOTE: no run-identity card here. DispatchTabsShell already renders the
  // identity card once at the top of the view (renderRunMeta), for every tab —
  // the single-country path (DossierRunBody) likewise renders none of its own.
  // A card here duplicated the top one for EU/NI (grouped) tabs only.

  if (agg === 'queued') {
    // Sequential run: this EU dossier is still waiting its turn. Show the queue
    // banner, NOT the drafting/polling panel — matches the standard per-country
    // path (RunStateBody). (Bug: queued was lumped with running, so a queued EU
    // tab showed "Drafting in Progress" while an earlier country was still running.)
    return <QueuedBanner country={country} />;
  }
  if (agg === 'running') {
    return (
      <>
        <AgentPollingPanel
          startedAt={rep.data?.started_at ?? null}
          workflowSteps={workflowForRun(rep.data).steps}
          stepSeconds={DOSSIER_STEP_SECONDS}
          title={
            <>
              Dossier Drafting in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          }
          subtitle="Compiling the regulatory submission dossier via the REGARA AI agent."
        />
      </>
    );
  }
  if (agg === 'failed') {
    return (
      <DispatchFailedBanner
        country={country}
        message="One or more EU dossier annexes failed to generate. Try starting a new run."
      />
    );
  }

  // Completed: render the COMPLETE dossier as US-style React sections — full-width,
  // each annex / deterministic part rendered with its own app-styled renderer
  // (same data the PDF is built from, via the JSON endpoint) + merged traceability matrix.
  return (
    <DossierRunProvenanceContext.Provider
      value={{
        generatedAt: lastCompletedAt,
        generatedBy: rep.data?.created_by_name ?? null,
      }}
    >
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              Dossier Complete
              <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {lastCompletedAt ? formatServerDateShort(lastCompletedAt) : '—'}
              {groupDurationSec !== null && ` · ${formatDuration(groupDurationSec)}`}
            </p>
          </div>
        </div>
      </div>
      {rep.data?.product_id != null && rep.data?.submission_type && (
        (country ?? '').toUpperCase() === 'NI' && NI_IVDR_CLASSES.has(rep.data.submission_type) ? (
          <CombinedNiIvdrDossierView
            productId={rep.data.product_id}
            deviceClass={rep.data.submission_type}
            runId={rep.data.id}
            productType={rep.data.dossier_response?.product_type ?? null}
          />
        ) : (
          <CombinedEuDossierView
            productId={rep.data.product_id}
            deviceClass={rep.data.submission_type}
            country={country ?? 'EU'}
            runId={rep.data.id}
            productType={rep.data.dossier_response?.product_type ?? null}
          />
        )
      )}
      <TraceabilityMatrix manifest={mergedProvenance} />
      <ResultDisclaimer />
    </DossierRunProvenanceContext.Provider>
  );
}

function ResultDisclaimer() {
  return (
    <footer className="mt-7 py-[18px] px-[22px] bg-state-warning-bg-soft border border-state-warning-border rounded-xl text-[#713f12] text-base leading-[1.6]">
      <strong className="block text-xs font-bold uppercase tracking-[0.22em] text-state-warning-text mb-1.5">
        Disclaimer.
      </strong>
      This is REGARA AI-drafted dossier decision-support, not legal or regulatory
      advice. Regulatory decisions rest with the relevant authority. Review every section with a qualified
      regulatory professional before submission.
    </footer>
  );
}

function friendlyErrorMessage(raw: string | null): string {
  const msg = (raw ?? '').toString();
  if (!msg) {
    return 'The dossier generation did not complete. Try running it again from the product list.';
  }
  if (/no JSON|did not produce/i.test(msg)) {
    return 'The agent did not produce a valid dossier. Try starting a new run.';
  }
  if (/timeout|timed out/i.test(msg)) {
    return 'The dossier generation took too long to complete. Try starting a new run.';
  }
  if (/auth|api[_ ]?key|unauthor/i.test(msg)) {
    return 'The agent could not reach the dossier service. Please contact support.';
  }
  return 'Something went wrong while generating the dossier. Try starting a new run, or contact support if this keeps happening.';
}
